<template>
  <div class="medicine-class-edit">
    <validation-observer class="validation-observer" v-slot="{ invalid }">
      <div class="title">{{ pageTitle }}</div>
      <div class="contents">
        <div class="content-wrap">
          <div class="heading">基本項目</div>
          <div class="content">
            <medicine-class-input-form
              class="input-form"
              :editClass="editClass"
              :target="target"
              :largeClass="largeClass"
              :largeClassesSelectData="largeClassesSelectData"
              :mediumClass="mediumClass"
              :mediumClassesSelectData="mediumClassesSelectData"
              @select-input="handleSelectInput"
              @text-input="handleTextInput"
            />
          </div>
        </div>
        <div class="button-wrap">
          <base-button-medium-white class="button" @click="goToListPage"
            >戻る</base-button-medium-white
          >
          <base-button-medium-orange
            class="button"
            v-if="lookOnlyFlg === 0"
            :disabled="invalid || waitFlg"
            @click="checkParentChanged"
            >登録</base-button-medium-orange
          >
        </div>
      </div>
      <announce-popup
        v-if="warningPopup.alertFlg"
        :type="warningPopup.type"
        :title="warningPopup.title"
        :buttons="warningPopup.buttons"
        :disabled="waitFlg"
        @cancel="warningPopup.alertFlg = false"
        @decision="updateData"
        >{{ warningPopup.popupMessage }}</announce-popup
      >
      <announce-popup
        v-if="alertFlg"
        :type="type"
        :title="title"
        :buttons="buttons"
        @close="closeAlert"
        >{{ popupMessage }}</announce-popup
      >
      <unsaved-leave-popup />
    </validation-observer>
  </div>
</template>

<script>
import MedicineClassInputForm from '@/components/parts/organisms/MedicineClassInputForm'
import BaseButtonMediumWhite from '@/components/parts/atoms/BaseButtonMediumWhite'
import BaseButtonMediumOrange from '@/components/parts/atoms/BaseButtonMediumOrange'
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import UnsavedLeavePopup from '@/components/popups/UnsavedLeavePopup'
import CheckInputDifference from '@/components/mixins/CheckInputDifference'
import { ValidationObserver } from 'vee-validate'
import { mapGetters } from 'vuex'
import SlideListRoute from '@/components/mixins/SlideListRoute'

export default {
  name: 'MedicineClassEdit',

  components: {
    MedicineClassInputForm,
    BaseButtonMediumWhite,
    BaseButtonMediumOrange,
    AnnouncePopup,
    UnsavedLeavePopup,
    ValidationObserver
  },

  mixins: [CheckInputDifference, SlideListRoute],

  props: {
    originalId: { type: Number },
    target: { type: String }
  },

  data() {
    return {
      editClass: {},
      largeClass: {},
      mediumClass: {},
      largeClassesSelectData: [],
      mediumClassesSelectData: [],
      waitFlg: false,
      alertFlg: false,
      type: '',
      title: '',
      popupMessage: '',
      buttons: ['閉じる'],
      noDataFlg: false,
      outdatedFlg: false,
      warningPopup: {
        alertFlg: false,
        type: 'alert',
        title: '注意',
        buttons: ['キャンセル', '変更する'],
        popupMessage: ''
      }
    }
  },

  computed: {
    ...mapGetters({
      lookOnlyFlg: 'auth/lookOnlyFlg'
    }),
    storeName() {
      return this.target === 'medicine-large-classes'
        ? 'medicineLargeClasses'
        : this.target === 'medicine-medium-classes'
        ? 'medicineMediumClasses'
        : 'medicineSmallClasses'
    },
    targetName() {
      return this.target === 'medicine-large-classes'
        ? '薬剤物品大分類'
        : this.target === 'medicine-medium-classes'
        ? '薬剤物品中分類'
        : '薬剤物品小分類'
    },
    pageTitle() {
      return this.targetName + '編集'
    }
  },

  created() {
    this.editClass = {
      ...this.$store.getters[`${this.storeName}/getDataByOriginalId`](
        this.originalId
      )
    }
    this.setParentData()
    this.mixinInputData = {
      editClass: this.editClass,
      largeClass: this.largeClass,
      mediumClass: this.mediumClass
    }
    this.mixinSetInitialData()
  },

  methods: {
    setMediumClassesSelectData() {
      const filteredMediumClasses = this.$store.getters[
        'medicineMediumClasses/getData'
      ]
        .filter(
          item =>
            item.medicineLargeClassOriginalId === this.largeClass.originalId
        )
        .map(item => ({ id: item.id, name: item.name }))
      this.mediumClassesSelectData = [{ id: 0, name: '新規分類名' }].concat(
        filteredMediumClasses
      )
    },
    setParentData() {
      if (this.target === 'medicine-medium-classes') {
        this.largeClass = {
          ...this.$store.getters['medicineLargeClasses/getDataByOriginalId'](
            this.editClass.medicineLargeClassOriginalId
          )
        }
        this.largeClassesSelectData = this.$store.getters[
          'medicineLargeClasses/selectDataZero'
        ]
      } else if (this.target === 'medicine-small-classes') {
        this.mediumClass = {
          ...this.$store.getters['medicineMediumClasses/getDataByOriginalId'](
            this.editClass.medicineMediumClassOriginalId
          )
        }
        this.largeClass = {
          ...this.$store.getters['medicineLargeClasses/getDataByOriginalId'](
            this.mediumClass.medicineLargeClassOriginalId
          )
        }
        this.setMediumClassesSelectData()
        this.largeClassesSelectData = this.$store.getters[
          'medicineLargeClasses/selectDataZero'
        ]
      }
    },
    handleSelectInput({ field, val }) {
      if (field === 'largeClass') {
        if (val === 0) {
          this.largeClass = { id: 0, originalId: 0, name: '' }
          this.mediumClass = { id: 0, originalId: 0, name: '' }
          this.mixinInputData.largeClass = this.largeClass
          this.mixinInputData.mediumClass = this.mediumClass
        } else {
          const target = this.$store.getters[
            'medicineLargeClasses/getDataById'
          ](val)
          Object.keys(target).forEach(key => {
            this.largeClass[key] = target[key]
          })
          this.mediumClass = { id: 0, originalId: 0, name: '' }
        }
        this.setMediumClassesSelectData()
      } else if (field === 'mediumClass') {
        if (val === 0) {
          this.mediumClass = { id: 0, originalId: 0, name: '' }
          this.mixinInputData.mediumClass = this.mediumClass
        } else {
          const target = this.$store.getters[
            'medicineMediumClasses/getDataById'
          ](val)
          Object.keys(target).forEach(key => {
            this.mediumClass[key] = target[key]
          })
        }
      }
    },
    handleTextInput({ field, val }) {
      if (field === 'largeClass') {
        this.largeClass.name = val
      } else if (field === 'mediumClass') {
        this.mediumClass.name = val
      }
    },
    goToListPage() {
      const mixinArguments =
        this.target === 'medicine-large-classes'
          ? ['large', this.originalId]
          : this.target === 'medicine-medium-classes'
          ? ['medium', this.originalId]
          : this.target === 'medicine-small-classes'
          ? ['small', this.originalId]
          : []
      const query = this.mixinMakeMedicineRouteQuery(...mixinArguments)
      this.$router.push({ name: 'medicines', query })
    },
    checkParentChanged() {
      if (this.target === 'medicine-medium-classes') {
        if (
          this.editClass.medicineLargeClassOriginalId !==
          this.largeClass.originalId
        ) {
          this.warningPopup.popupMessage =
            '所属している大分類先を変更すると、\n中分類内の全ての小分類・薬剤物品も一緒に移動されます。\n所属している大分類先を変更してもよろしいですか？'
          this.warningPopup.alertFlg = true
        } else {
          this.updateData()
        }
      } else if (this.target === 'medicine-small-classes') {
        if (
          this.editClass.medicineMediumClassOriginalId !==
          this.mediumClass.originalId
        ) {
          this.warningPopup.popupMessage =
            '所属している中分類先を変更すると、\n小分類内の全ての薬剤物品も一緒に移動されます。\n所属している中分類先を変更してもよろしいですか？'
          this.warningPopup.alertFlg = true
        } else {
          this.updateData()
        }
      } else {
        this.updateData()
      }
    },
    async updateData() {
      this.waitFlg = true
      const result = await this.$store.dispatch(`${this.storeName}/update`, {
        editClass: this.editClass,
        largeClass: this.largeClass,
        mediumClass: this.mediumClass
      })
      if (result === true) {
        this.type = 'success'
        this.title = '完了'
        this.popupMessage = '編集しました'
        this.mixinSetInitialData()
      } else {
        this.type = 'failure'
        this.title = '失敗'
        if (result === 'already edited') {
          this.popupMessage = `編集に失敗しました。\n編集中の${this.targetName}は別端末で編集されました。`
          this.outdatedFlg = true
        } else if (result === 'already deleted') {
          this.popupMessage = `編集に失敗しました。\n編集中の${this.targetName}は既に削除されています。`
          this.noDataFlg = true
        } else if (result === 'no data in clinic') {
          this.popupMessage =
            '編集に失敗しました。\n薬剤物品分類は既に削除されています。'
          this.noDataFlg = true
        } else if (result === 'no medicineMediumClass') {
          this.noDataFlg = true
          this.popupMessage =
            '編集に失敗しました。\n薬剤物品中分類は別端末で編集または削除されました。'
        } else if (result === 'no medicineLargeClass') {
          this.noDataFlg = true
          this.popupMessage =
            '編集に失敗しました。\n薬剤物品大分類は別端末で編集または削除されました。'
        } else {
          this.popupMessage = '編集に失敗しました'
        }
      }
      this.waitFlg = false
      this.alertFlg = true
    },
    closeAlert() {
      this.alertFlg = false
      if (this.type === 'success') {
        this.$store.dispatch('petorelu/okLeave')
        this.goToListPage()
      } else if (this.outdatedFlg) {
        this.$store.dispatch('petorelu/okLeave')
        this.goToListPage()
      } else if (this.noDataFlg) {
        this.$store.dispatch('petorelu/okLeave')
        this.goToListPage()
      }
      this.title = ''
      this.type = ''
      this.popupMessage = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.medicine-class-edit {
  width: 100%;
  min-width: 614px;
  text-align: left;
  > .validation-observer {
    > .title {
      font-size: 20px;
      font-weight: bold;
      border-bottom: 1px solid #{$light-grey};
      padding-bottom: 20px;
    }
    > .contents {
      margin-top: 20px;
      > .content-wrap {
        > .heading {
          font-size: 15px;
          font-weight: bold;
          border-bottom: 1px solid #{$light-grey};
          padding-bottom: 20px;
        }
        > .content {
          margin-top: 25px;
          > .input-form {
            width: 600px;
          }
        }
      }
      > .button-wrap {
        display: flex;
        justify-content: center;
        margin: 60px 0;
        > .button {
          margin: 0 16px;
        }
      }
    }
  }
}
</style>
